import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../../providers/supabase/supabase";

/**
 * Textos base del guion corto por producto (del prototipo: PLAY, GUI y HORA).
 * Las promesas viven en la BD (sales_texts, "Textos por validar"); esto queda como
 * respaldo para productos sin textos y como semilla (migración 20260925100100).
 */
export type Play = {
  dec: string; // con quién hablar
  proof: string; // prueba social ("distribuidoras", "restaurantes"…)
  pitch: string; // propuesta en una frase
  spin: [string, string][]; // [paso, pregunta]
};

export const PLAYBOOK: Record<string, Play> = {
  storcky: {
    dec: "dueño o encargado de almacén / compras",
    proof: "distribuidoras",
    pitch:
      "Te enseño en 15 minutos cómo saber qué te falta antes de que un cliente te lo pida, sin cambiar tu forma de trabajar.",
    spin: [
      [
        "Situación",
        "¿Cómo llevan hoy el inventario: sistema, Excel o libreta?",
      ],
      [
        "Problema",
        "¿Qué tan seguido un cliente pide algo y resulta que no hay?",
      ],
      [
        "Implicación",
        "Cuando pasa, ¿ese cliente se va a comprar a otro lado? ¿De cuánto es un pedido promedio?",
      ],
      [
        "Beneficio",
        "Si supiera qué se va a acabar una semana antes, ¿qué cambiaría para ustedes?",
      ],
    ],
  },
  mooi: {
    dec: "dueño o gerente",
    proof: "restaurantes",
    pitch:
      "Te traemos clientes de tu colonia en los días flojos, y tú decides la promoción. Sin comisión por pedido.",
    spin: [
      ["Situación", "¿Cómo les llegan hoy los clientes nuevos?"],
      ["Problema", "¿Qué días están más flojos?"],
      [
        "Implicación",
        "Una mesa vacía entre semana, ¿cuánto dejan de vender al mes?",
      ],
      [
        "Beneficio",
        "Si llenara 10 mesas más a la semana sin pagar comisión, ¿qué haría con ese dinero?",
      ],
    ],
  },
  kelmo: {
    dec: "dueño o jefe técnico",
    proof: "recargadoras",
    pitch:
      "Tus constancias y bitácoras listas en un clic, y tus clientes reciben el aviso de recarga solos: más recargas, menos papel.",
    spin: [
      ["Situación", "¿Cómo hacen hoy constancias, etiquetas y bitácoras?"],
      ["Problema", "¿Cuánto tiempo a la semana se les va en capturarlas?"],
      [
        "Implicación",
        "¿Les ha pasado que un cliente no recarga a tiempo y lo pierden, o lo multan en una inspección?",
      ],
      [
        "Beneficio",
        "Si cada cliente recibiera solo su aviso de recarga, ¿cuántas recargas más harían al año?",
      ],
    ],
  },
  telemetria: {
    dec: "director de operaciones o mantenimiento",
    proof: "empresas de su tipo",
    pitch:
      "Te propongo una entrevista de 20 minutos para medir dónde se pierde tiempo o equipo; si hay caso, un piloto pequeño.",
    spin: [
      [
        "Situación",
        "¿Cómo ubican hoy su equipo y a su personal en planta o piso?",
      ],
      [
        "Problema",
        "¿Cuánto tiempo al día se pierde buscando algo o a alguien?",
      ],
      [
        "Implicación",
        "¿Cuánto les cuesta un equipo perdido o una hora de un técnico buscando?",
      ],
      [
        "Beneficio",
        "Si lo vieran en un mapa en tiempo real, ¿dónde lo usarían primero?",
      ],
    ],
  },
  legal: {
    dec: "dueño o responsable de RR.HH.",
    proof: "empresas de su tamaño",
    pitch:
      "En una sesión te digo qué te pueden multar hoy y cuánto cuesta arreglarlo.",
    spin: [
      [
        "Situación",
        "¿Todos sus trabajadores tienen contrato firmado y reglamento interior?",
      ],
      [
        "Problema",
        "¿Han tenido demandas, renuncias conflictivas o inspecciones?",
      ],
      [
        "Implicación",
        "Una demanda laboral promedio, entre salarios caídos y abogados, ¿cuánto les costaría hoy?",
      ],
      [
        "Beneficio",
        "Si en una sesión supiera exactamente qué le pueden multar, ¿lo arreglaría ahora o esperaría?",
      ],
    ],
  },
  landing: {
    dec: "dueño o encargado de marketing",
    proof: "negocios de su zona",
    pitch:
      "Te hago una página que convierte visitas en mensajes de WhatsApp, lista en una semana.",
    spin: [
      ["Situación", "¿Cómo los encuentran hoy sus clientes nuevos?"],
      [
        "Problema",
        "¿Cuántos mensajes o llamadas les llegan por internet a la semana?",
      ],
      [
        "Implicación",
        "¿Cuántos clientes cree que se van con la competencia que sí aparece en Google?",
      ],
      [
        "Beneficio",
        "Si le llegaran 5 mensajes más a la semana por WhatsApp, ¿cuántos cerraría?",
      ],
    ],
  },
  norvia: {
    dec: "director de operaciones",
    proof: "empresas de seguridad",
    pitch:
      "Tus clientes reciben su reporte solo y tú sabes en todo momento quién está en su punto.",
    spin: [
      ["Situación", "¿Cómo controlan hoy turnos, rondines e incidencias?"],
      [
        "Problema",
        "¿Qué pasa cuando un cliente les pide evidencia de un rondín?",
      ],
      [
        "Implicación",
        "¿Han perdido o casi perdido un contrato por falta de reportes?",
      ],
      [
        "Beneficio",
        "Si cada cliente recibiera su reporte solo, ¿cómo lo usaría para vender más?",
      ],
    ],
  },
};

// Guion genérico para productos nuevos sin textos base
export const GENERIC_PLAY: Play = {
  dec: "dueño o quien toma las decisiones",
  proof: "empresas de su giro",
  pitch:
    "Le cuento en 15 minutos cómo lo resolvemos para negocios como el suyo.",
  spin: [
    ["Situación", "¿Cómo lo resuelven hoy?"],
    ["Problema", "¿Qué es lo que más les cuesta de eso?"],
    ["Implicación", "¿Cuánto les cuesta al mes cuando pasa?"],
    ["Beneficio", "Si eso se resolviera, ¿qué cambiaría para ustedes?"],
  ],
};

// Horario sugerido por giro (prototipo); con suficientes llamadas, el real de best_hours
const HORA: Record<string, string> = {
  Restaurantes: "martes a jueves, 16:00–17:30 (antes del servicio de la noche)",
  "Comercio Mayorista": "martes o jueves, 17:00–18:00",
  Salud: "lunes a miércoles, 13:00–14:00",
  Manufactura: "martes a jueves, 9:00–10:00",
  Hospedaje: "miércoles, 11:00–12:00",
};
export const bestHour = (sector?: string | null) =>
  HORA[sector ?? ""] ?? "jueves, 17:00–18:00";

// Con 30 llamadas o más, el mejor día × hora medido (más decisores alcanzados)
const MIN_CALLS = 30;
const DIAS = [
  "",
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
  "domingo",
];
type Slot = { dow: number; hour: number; calls: number; reached: number };
export const measuredHour = (slots: Slot[]) => {
  if (slots.reduce((s, x) => s + x.calls, 0) < MIN_CALLS) return null;
  const best = slots
    .filter((x) => x.calls >= 3)
    .sort(
      (a, b) => b.reached / b.calls - a.reached / a.calls || b.calls - a.calls,
    )[0];
  return best && best.reached > 0
    ? `${DIAS[best.dow]}, ${best.hour}:00–${best.hour + 1}:00 (tu mejor hora medida)`
    : null;
};

/** Horario sugerido: el medido en tus llamadas si ya hay suficientes; si no, por giro. */
export const useBestHour = () => {
  const { data } = useQuery({
    queryKey: ["best_hours"],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data } = await getSupabaseClient().from("best_hours").select("*");
      return measuredHour((data ?? []) as Slot[]);
    },
  });
  return (sector?: string | null) => data ?? bestHour(sector);
};

/** Siguiente mejor acción (nba() del prototipo) según etapa, enfriamiento y horario. */
export const nba = ({
  dnc,
  stage,
  offerName,
  bestOfferName,
  idle,
  decay,
  hora,
}: {
  dnc: boolean;
  stage?: string | null;
  offerName?: string;
  bestOfferName?: string;
  idle?: number | null;
  decay?: number | null;
  hora: string;
}) => {
  if (dnc) return "No contactar: está en tu lista de bajas.";
  if (!stage)
    return `Pásala al CRM${bestOfferName ? ` en ${bestOfferName}` : ""} e inscríbela en la cadencia. Primera llamada: ${hora}.`;
  const pre = decay
    ? `Lleva ${idle} días sin contacto y se está enfriando. `
    : "";
  const m: Record<string, string> = {
    nuevo: `Primera llamada ${hora}; si no contesta, WhatsApp con la plantilla ${offerName ?? ""} 1.`,
    contactado: `Segunda llamada en otro horario (${hora}) y pide hablar con quien decide.`,
    conversacion: "Agenda la demo: propón dos horarios concretos esta semana.",
    interesado: "Agenda el alta guiada: 20 minutos por videollamada.",
    entrevista:
      "Haz la entrevista de descubrimiento y mide el problema en pesos.",
    diagnostico:
      "Entrega el diagnóstico con 3 hallazgos y el costo de no atenderlos.",
    demo: "Envía la propuesta en menos de 24 h con el caso de ahorro de su giro.",
    piloto:
      "Define con él la métrica de éxito del piloto y la fecha de revisión.",
    onboarding: "Acompaña la primera semana: una llamada de 10 min el día 3.",
    preventa: "Presenta la preventa con fecha límite y lugares limitados.",
    propuesta: "Llama para resolver objeciones y fija fecha de arranque.",
    ganado: "Pide referidos y ofrece la siguiente oferta que le hace match.",
    activo: "Pide referidos y ofrece la siguiente oferta que le hace match.",
    perdido: "Reintenta en 90 días con un ángulo distinto.",
  };
  return (
    pre +
    (m[stage] ??
      "Define con el cliente el siguiente paso y agéndalo con fecha.")
  );
};

/** Lo que usan los textos de la ficha (datos del DENUE o de la empresa). */
export type PlayCo = {
  nombre: string;
  municipio: string;
  actividad: string;
  rango: string;
  sector: string;
  sitio: string | null;
};

/**
 * Match y propuesta + guion completo (prototipo: PLAY y GUI). Las promesas se
 * validan en "Textos por validar" (sales_texts); esto es el respaldo.
 */
export type Proposal = {
  need: (c: PlayCo) => string;
  sol: string;
  open: (c: PlayCo) => string;
  obj: [string, string][];
  price: string;
  next: string;
  quant: string;
  risk: string;
  listen: string[]; // "qué escuchar" de cada paso SPIN
};

const giro = (c: PlayCo) =>
  c.actividad.toLowerCase().replace("comercio al por mayor de ", "");

export const PROPOSAL: Record<string, Proposal> = {
  storcky: {
    need: (c) =>
      `Mueve ${giro(c)} con ${c.rango} personas. Lo común en su giro: inventario en Excel o libreta, faltantes que se notan cuando el cliente ya pidió y pedidos sueltos por WhatsApp.`,
    sol: "Storcky le da inventario en tiempo real, alertas de faltantes, pedidos y cotizaciones desde el celular, con sus propios productos importados de su Excel.",
    open: (c) =>
      `Hola, ¿hablo con quien lleva el almacén de ${c.nombre}? Trabajo con distribuidoras de ${c.municipio} que perdían ventas por faltantes y lo resolvieron en una semana.`,
    obj: [
      [
        "Ya uso Excel",
        "Perfecto: importamos tu Excel el primer día, no empiezas de cero.",
      ],
      [
        "No tengo tiempo",
        "La demo dura 15 minutos y usa tus productos reales.",
      ],
      ["Está caro", "Un solo faltante evitado al mes paga la suscripción."],
    ],
    price:
      "Plan de arranque mensual con configuración incluida (usa tu Kit de Arranque).",
    next: "Demo de 15 min por videollamada esta semana.",
    quant:
      "Si pierden 2 pedidos de $3,000 a la semana por faltantes, son unos $24,000 al mes. Storcky cuesta una fracción de eso.",
    risk: "Configuración incluida y te importamos tu Excel el primer día; si en 30 días no ves menos faltantes, lo dejas.",
    listen: [
      "Si dice Excel o libreta, ya tienes el dolor.",
      "Busca un número: “una o dos veces por semana”.",
      "Aquí sale el costo del problema en pesos.",
      "Deja que él diga el beneficio con sus palabras.",
    ],
  },
  mooi: {
    need: (c) =>
      `Negocio de consumo local en ${c.municipio}: necesita llenar mesas entre semana y depender menos de las apps de reparto y sus comisiones.`,
    sol: "MOOI lo pone frente a clientes de su zona con promociones propias y sin comisión por pedido.",
    open: (c) =>
      `Hola, vi ${c.nombre} en ${c.municipio}. ¿Cómo les va de martes a jueves? Estamos ayudando a restaurantes de la zona a llenar esos días.`,
    obj: [
      [
        "Ya estoy en apps de reparto",
        "MOOI no compite con ellas: trae clientes a tu mesa, sin su comisión.",
      ],
      [
        "No tengo quién lo maneje",
        "Lo dejamos listo en 20 minutos; publicar una promoción toma un minuto.",
      ],
    ],
    price: "Alta sin costo y plan mensual después del primer mes.",
    next: "Alta guiada de 20 min por videollamada.",
    quant: "10 mesas más a la semana con ticket de $450 son $18,000 al mes.",
    risk: "Alta sin costo; el primer mes pruebas sin compromiso.",
    listen: [
      "Redes, apps de reparto, recomendación.",
      "Casi siempre martes a jueves.",
      "Que calcule él: mesas × ticket × días.",
      "Cierra con su respuesta.",
    ],
  },
  kelmo: {
    need: () =>
      "Da servicio sujeto a NOM (extintores): debe entregar constancias, etiquetas y bitácoras, y hoy probablemente las hace en papel o Word.",
    sol: "KELMO genera constancias, bitácoras y recordatorios de recarga por cliente, listas para inspección.",
    open: (c) =>
      `Hola, ¿con quién veo el tema de las constancias de servicio en ${c.nombre}? Tengo una herramienta hecha para recargadoras.`,
    obj: [
      ["Lo hago en Word", "Importamos tus formatos; el cambio es de un día."],
      ["Somos pocos", "Justo por eso: te ahorra horas de captura por semana."],
    ],
    price: "Plan mensual por número de técnicos.",
    next: "Demo de 20 min con una constancia real suya.",
    quant:
      "Recuperar 5 recargas al mes que hoy se olvidan paga KELMO varias veces.",
    risk: "Importamos tus formatos actuales; la primera constancia la haces en la demo.",
    listen: [
      "Word, Excel o a mano.",
      "Horas de técnico o de oficina.",
      "Cliente perdido = recarga anual perdida.",
      "Que ponga el número.",
    ],
  },
  telemetria: {
    need: (c) =>
      `Opera ${c.sector === "Salud" ? "equipo médico móvil en áreas grandes" : c.sector === "Transporte" ? "activos móviles y patios" : "espacios amplios con equipo y personal en movimiento"}: pierde tiempo buscando cosas o personas y no sabe dónde se va.`,
    sol: "La telemetría BLE ubica equipo y personal en tiempo real con balizas de bajo costo y reportes de uso.",
    open: (c) =>
      `Hola, en ${c.nombre} ¿cuánto tiempo al día se va en buscar equipo o personal? Estamos midiendo eso en empresas de ${c.municipio}.`,
    obj: [
      [
        "Ya tenemos cámaras",
        "Las cámaras ven, pero no te dicen dónde está cada cosa ni cuánto se usa.",
      ],
      ["Suena caro", "El piloto es chico y se mide contra un ahorro concreto."],
    ],
    price: "Piloto de 30 días con precio cerrado; después, por baliza.",
    next: "Entrevista de descubrimiento de 20 min.",
    quant: "20 personas perdiendo 15 minutos al día son 100 horas al mes.",
    risk: "Piloto de 30 días, precio cerrado y una métrica de éxito que acordamos antes.",
    listen: [
      "Radio, llamadas, caminando.",
      "Minutos por persona × personas.",
      "Lleva la conversación a pesos.",
      "Ese es tu piloto.",
    ],
  },
  legal: {
    need: (c) =>
      `Con ${c.rango} empleados ya le aplican reglamento interior de trabajo, NOM-035 y contratos al día; casi nadie de su tamaño lo tiene completo.`,
    sol: "Diagnóstico laboral y corporativo con plan de acción y documentos listos.",
    open: (c) =>
      `Hola, ¿quién ve los temas laborales en ${c.nombre}? Estamos haciendo diagnósticos rápidos a empresas de ${c.municipio} por la revisión de NOM-035.`,
    obj: [
      [
        "Ya tengo contador",
        "El contador ve lo fiscal; esto es laboral y corporativo.",
      ],
      [
        "No hemos tenido problemas",
        "El diagnóstico es para que siga así; una inspección no avisa.",
      ],
    ],
    price: "Diagnóstico de precio fijo, acreditable a la iguala.",
    next: "Sesión de diagnóstico de 30 min.",
    quant: "Una sola demanda puede costar más de un año de iguala.",
    risk: "Diagnóstico de precio fijo y acreditable si contrata la iguala.",
    listen: [
      "Casi nunca está completo.",
      "Busca la historia reciente.",
      "Que dimensione el riesgo.",
      "Lleva al diagnóstico.",
    ],
  },
  landing: {
    need: (c) =>
      c.sitio
        ? `Tiene sitio (${c.sitio}) pero no convierte: sin formulario, sin WhatsApp directo ni medición.`
        : "No tiene sitio web: sus clientes lo buscan en Google y encuentran a la competencia.",
    sol: "Landing page profesional en Framer, lista en días, con WhatsApp, formulario y medición.",
    open: (c) =>
      `Hola, busqué ${c.actividad.toLowerCase()} en ${c.municipio} y ${c.nombre} ${c.sitio ? "aparece, pero su página no invita a contactarlos" : "no aparece"}. ¿Le enseño cómo se ve su competencia?`,
    obj: [
      [
        "Ya tengo Facebook",
        "Facebook no aparece bien en Google; la página sí, y es tuya.",
      ],
      ["Lo hace mi sobrino", "Te dejo una muestra gratis para comparar."],
    ],
    price: "Precio cerrado por página + mantenimiento opcional.",
    // "en 48 h" se quitó (29/09): no es un dato real de la ficha de Raftel Studio
    // (offer_facts.garantia solo dice "Muestra de diseño antes de pagar.", sin plazo).
    next: "Enviar muestra de diseño antes de que pague.",
    quant: "Con que la página traiga un cliente al mes, se paga sola.",
    risk: "Te mando una muestra de diseño antes de que pagues.",
    listen: [
      "Recomendación, Facebook, Google.",
      "Si son pocos, ahí está.",
      "Muéstrale a su competencia.",
      "Que calcule el retorno.",
    ],
  },
  norvia: {
    need: () =>
      "Opera guardias o monitoreo: le cuesta controlar turnos, rondines e incidencias y entregar reportes a sus clientes.",
    sol: "NORVIA controla turnos, rondines con evidencia y reportes automáticos para cada cliente.",
    open: (c) =>
      `Hola, ¿quién coordina los turnos en ${c.nombre}? Estamos abriendo la preventa de una herramienta para empresas de seguridad.`,
    obj: [
      [
        "Usamos WhatsApp",
        "Funciona hasta que un cliente pide evidencia; NORVIA la guarda sola.",
      ],
    ],
    price: "Preventa con precio de fundador.",
    next: "Entrevista de 20 min y lugar en la preventa.",
    quant: "Retener un solo contrato paga NORVIA todo el año.",
    risk: "Precio de fundador y lugar limitado en la preventa.",
    listen: [
      "WhatsApp, papel, llamadas.",
      "Normalmente no la tienen a la mano.",
      "Es su mayor miedo.",
      "Diferenciador comercial.",
    ],
  },
};

export const GENERIC_PROPOSAL: Proposal = {
  need: (c) =>
    `${c.nombre} (${giro(c)}) encaja en el segmento de este producto.`,
  sol: "Se lo resolvemos con este producto, ajustado a su operación.",
  open: (c) =>
    `Hola, ¿hablo con quien decide en ${c.nombre}? Trabajo con empresas de ${c.municipio} de su giro.`,
  obj: [],
  price: "Según su operación.",
  next: "Reunión de 15 min esta semana.",
  quant: "Pon el costo del problema en pesos con sus propios números.",
  risk: "Una prueba corta antes de comprometerse.",
  listen: ["", "", "", ""],
};

/** Canal sugerido (prototipo: canalDe). */
export const bestChannel = (emailTipo: string | null, tel: string | null) =>
  emailTipo === "personal"
    ? "WhatsApp al dueño (su correo es personal: probablemente él mismo lo lee)"
    : tel
      ? "Llamada y, si no contesta, WhatsApp"
      : "Correo corporativo";
